import Quill from "quill";
import { ClassAttributor, Scope } from 'parchment';
import dayjs from "dayjs";
// @ts-ignore - html-to-pdfmake doesn't have type definitions
import htmlToPdfmake from 'html-to-pdfmake';
// @ts-ignore - pdfmake doesn't have complete type definitions
import pdfMake from 'pdfmake/build/pdfmake';
// @ts-ignore - pdfmake fonts don't have type definitions
import pdfFonts from 'pdfmake/build/vfs_fonts';

import {
  EDITOR_FONT_SIZES,
  EDITOR_HEADING_STYLES,
  FILE_EXPORT_PARAMS,
  QUILL_HR_EXPORT_LINE_CLASS,
  TABLE_BORDERLESS_CLASS,
  TABLE_BORDERLESS_BLOCK_CLASS,
  TABLE_TEXT_RIGHT_CLASS,
  TABLE_TEXT_CENTER_CLASS
} from "../../config/documentEditor";
import { applyChatPdfTableFitStylesToHtml } from "../../utils/markdownUtils";

// Initialize pdfMake with fonts
(pdfMake as any).vfs = pdfFonts as any;

// Custom table layout for <hr>: only draws the bottom line (so the line is visible in footer/header/body)
const pdfMakeApi = pdfMake as any;
pdfMakeApi.tableLayouts = {
    ...pdfMakeApi.tableLayouts,
    hrLineLayout: {
        hLineWidth: (i: number, node: any) => (i === 1 ? (node.hrLineWidth ?? 0.5) : 0),
        vLineWidth: () => 0,
        hLineColor: (_i: number, node: any) =>
            node.hrLineColor ?? FILE_EXPORT_PARAMS.HR_LINE_COLOR,
        paddingLeft: () => 0,
        paddingRight: () => 0,
        paddingTop: () => 0,
        paddingBottom: () => 0,
    },
    /**
     * Letter-style layout tables (border:none, padding:0 on all cells). html-to-pdfmake ignores CSS padding;
     * pdfmake's default layout still adds ~4pt cell padding ? this layout removes padding and grid lines.
     */
    borderlessCompactTable: {
        hLineWidth: () => 0,
        vLineWidth: () => 0,
        paddingLeft: () => 0,
        paddingRight: () => 0,
        paddingTop: () => 0,
        paddingBottom: () => 0,
    },
};

export class QuillHelper {
    private static arialFontLoaded = false;

    private static async ensureArialFont(): Promise<void> {
        if (QuillHelper.arialFontLoaded) return;

        const loadVariant = async (path: string, vfsKey: string): Promise<void> => {
            const response = await fetch(path);
            const buffer = await response.arrayBuffer();
            const bytes = new Uint8Array(buffer);
            let binary = '';
            for (let i = 0; i < bytes.byteLength; i++) {
                binary += String.fromCharCode(bytes[i]);
            }
            (pdfMake as any).vfs[vfsKey] = btoa(binary);
        };

        await Promise.all([
            loadVariant('/assets/Arial.TTF',             'Arial.ttf'),
            loadVariant('/assets/Arial_Bold.TTF',        'Arial-Bold.ttf'),
            loadVariant('/assets/Arial_Italic.TTF',      'Arial-Italic.ttf'),
            loadVariant('/assets/Arial_Bold_Italic.TTF', 'Arial-BoldItalic.ttf'),
        ]);

        (pdfMake as any).fonts = {
            ...(pdfMake as any).fonts,
            Arial: {
                normal:      'Arial.ttf',
                bold:        'Arial-Bold.ttf',
                italics:     'Arial-Italic.ttf',
                bolditalics: 'Arial-BoldItalic.ttf',
            },
        };
        QuillHelper.arialFontLoaded = true;
    }

    static initFontSizes() {
        const SizeClass = new ClassAttributor('size', 'ql_size', {
            scope: Scope.INLINE,
            whitelist: EDITOR_FONT_SIZES,
        });
        Quill.register(SizeClass, true);
    }
    static initIcons() {
        const icons = Quill.import("ui/icons") as any;
        icons["undo"] = `<svg width="16" height="16" viewBox="0 0 24 24" fill="currentColor"><path d="M12.5 8c-2.65 0-5.05.99-6.9 2.6L2 7v9h9l-3.62-3.62c1.39-1.16 3.16-1.88 5.12-1.88 3.54 0 6.55 2.31 7.6 5.5l2.37-.78C21.08 11.03 17.15 8 12.5 8z"/></svg>`;
        icons["redo"] = `<svg width="16" height="16" viewBox="0 0 24 24" fill="currentColor"><path d="M18.4 10.6C16.55 8.99 14.15 8 11.5 8c-4.65 0-8.58 3.03-9.96 7.22L3.9 16c1.05-3.19 4.05-5.5 7.6-5.5 1.95 0 3.73.72 5.12 1.88L13 16h9V7l-3.6 3.6z"/></svg>`;
        icons["download"] = `<svg width="18" height="18" viewBox="0 0 64 64"><text x="32" y="42" text-anchor="middle" font-size="26" font-weight="900" font-family="Arial, sans-serif" fill="currentColor" font-weight="bold">PDF</text></svg>`;
        icons["download-word"] = `<svg width="18" height="18" viewBox="0 0 64 64"><text x="32" y="44" text-anchor="middle" font-size="36" font-family="Arial, sans-serif" fill="currentColor" font-weight="bold">W</text></svg>`;
        icons["close"] = `<svg width="16" height="16" viewBox="0 0 24 24" fill="currentColor"><path d="M19 6.41L17.59 5 12 10.59 6.41 5 5 6.41 10.59 12 5 17.59 6.41 19 12 13.41 17.59 19 19 17.59 13.41 12z"/></svg>`;
        icons["toggle-position"] = `<svg width="16" height="16" viewBox="0 0 24 24" fill="currentColor"><g transform="rotate(90 12 12)"><path d="M9 3L5 6.99h3V14h2V6.99h3L9 3zm7 14.01V10h-2v7.01h-3L15 21l4-3.99h-3z"/></g></svg>`;
        const chevron = `<svg width="9" height="9" viewBox="0 0 24 24" fill="currentColor" style="margin-left:3px;flex-shrink:0"><path d="M7 10l5 5 5-5H7z"/></svg>`;
        icons["more-options"] = `<span class="ql-btn-label">More${chevron}</span>`;
        icons["size"] = `<span class="ql-btn-label"><span class="ql-size-text">12</span>${chevron}</span>`;
        icons["header"] = `<span class="ql-btn-label"><span class="ql-header-text">Normal</span>${chevron}</span>`;
        icons["align-options"] = `<svg width="14" height="14" viewBox="0 0 24 24" fill="currentColor" style="flex-shrink:0"><path d="M3 18h18v-2H3v2zm0-5h18v-2H3v2zm0-7v2h18V6H3z"/></svg>${chevron}`;
    }

    static handleMoreOptionsItemClick(item: HTMLElement, quill: Quill): void {
        const action = item.dataset.action;

        if (action === 'undo') {
            quill.history.undo();
            return;
        }
        if (action === 'redo') {
            quill.history.redo();
            return;
        }

        const range = quill.getSelection(true);
        if (!range) return;

        switch (action) {
            case 'clean':
                quill.removeFormat(range.index, range.length);
                break;
            case 'blockquote': {
                const format = quill.getFormat(range);
                const isBlockquote = format.blockquote === true;
                quill.formatLine(range.index, range.length, 'blockquote', !isBlockquote);
                break;
            }
            case 'ordered-list': {
                const fmt = quill.getFormat(range);
                const value = fmt.list === 'ordered' ? false : 'ordered';
                QuillHelper.formatListSkipEmptyLines(quill, range, value);
                break;
            }
            case 'bullet-list': {
                const fmt = quill.getFormat(range);
                const value = fmt.list === 'bullet' ? false : 'bullet';
                QuillHelper.formatListSkipEmptyLines(quill, range, value);
                break;
            }
            case 'indent-decrease':
                quill.formatLine(range.index, range.length, 'indent', '-1');
                break;
            case 'indent-increase':
                quill.formatLine(range.index, range.length, 'indent', '+1');
                break;
            case 'link': {
                const fmt = quill.getFormat(range);
                const popup = document.getElementById('ql-link-popup') as HTMLElement | null;
                const input = document.getElementById('ql-link-input') as HTMLInputElement | null;

                if (!popup || !input) {
                    // Fallback if popup element is not in the DOM
                    if (fmt.link) {
                        quill.formatText(range.index, range.length, 'link', false);
                    } else {
                        const url = prompt('Enter URL:');
                        if (url) quill.formatText(range.index, range.length, 'link', url);
                    }
                    break;
                }

                // Store selection so the save handler can apply the link after popup closes
                popup.dataset.rangeIndex = String(range.index);
                popup.dataset.rangeLength = String(range.length);

                // Pre-fill with the existing URL if the selection already has a link
                input.value = typeof fmt.link === 'string' ? fmt.link : '';

                // Position popup below the More button, then flip if it overflows the right edge
                const moreBtn = document.querySelector('.ql-more-options') as HTMLElement | null;
                const btnRect = moreBtn?.getBoundingClientRect();
                if (btnRect) {
                    popup.style.top = `${btnRect.bottom + 4}px`;
                    popup.style.left = `${btnRect.left}px`;
                    popup.style.right = 'auto';
                }

                popup.style.display = 'flex';
                setTimeout(() => {
                    if (btnRect) {
                        const popupRect = popup.getBoundingClientRect();
                        if (popupRect.right > window.innerWidth - 8) {
                            popup.style.left = 'auto';
                            popup.style.right = `${window.innerWidth - btnRect.right}px`;
                        }
                    }
                    input.focus();
                    input.select();
                }, 10);
                break;
            }
            case 'subscript': {
                const fmt = quill.getFormat(range);
                quill.formatText(range.index, range.length, 'script', fmt.script === 'sub' ? false : 'sub');
                break;
            }
            case 'superscript': {
                const fmt = quill.getFormat(range);
                quill.formatText(range.index, range.length, 'script', fmt.script === 'super' ? false : 'super');
                break;
            }
        }
    }

    static handleSizeItemClick(item: HTMLElement, quill: Quill): void {
        const size = item.dataset.size;
        if (size) {
            const range = quill.getSelection(true);
            if (range) {
                quill.formatText(range, 'size', size);
            }
        }
    }

    static handleHeaderItemClick(item: HTMLElement, quill: Quill): void {
        const headerValue = item.dataset.header;

        if (headerValue !== undefined) {
            const range = quill.getSelection(true);

            if (range) {
                const headerLevel = headerValue === 'false' ? false : parseInt(headerValue);
                quill.formatLine(range.index, range.length, 'header', headerLevel);
            }
        }
    }

    private static formatListSkipEmptyLines(quill: Quill, range: { index: number; length: number }, value: 'ordered' | 'bullet' | false): void {
        if (range.length === 0) {
            quill.formatLine(range.index, 1, 'list', value);
            return;
        }
        const lines = quill.getLines(range.index, range.length);
        lines.forEach((line: any) => {
            if (line.length() > 1) {
                quill.formatLine(quill.getIndex(line), 1, 'list', value);
            }
        });
    }

    static handleAlignOptionsItemClick(item: HTMLElement, quill: Quill): void {
        const action = item.dataset.action;
        const range = quill.getSelection(true);
        if (!range) return;

        switch (action) {
            case 'align-left':
                quill.formatLine(range.index, range.length, 'align', false);
                break;
            case 'align-center':
                quill.formatLine(range.index, range.length, 'align', 'center');
                break;
            case 'align-right':
                quill.formatLine(range.index, range.length, 'align', 'right');
                break;
            case 'align-justify':
                quill.formatLine(range.index, range.length, 'align', 'justify');
                break;
            case 'indent-increase':
                quill.formatLine(range.index, range.length, 'indent', '+1');
                break;
            case 'indent-decrease':
                quill.formatLine(range.index, range.length, 'indent', '-1');
                break;
            case 'ordered-list': {
                const fmt = quill.getFormat(range);
                const value = fmt.list === 'ordered' ? false : 'ordered';
                QuillHelper.formatListSkipEmptyLines(quill, range, value);
                break;
            }
            case 'bullet-list': {
                const fmt = quill.getFormat(range);
                const value = fmt.list === 'bullet' ? false : 'bullet';
                QuillHelper.formatListSkipEmptyLines(quill, range, value);
                break;
            }
        }
    }

    /**
     * quill-table-better keeps only a fixed attribute set on TD/TH (no `class`).
     * Letter classes table-borderless-block / text-right are dropped on paste unless mirrored as inline style.
     */
    static inlineLetterTableSemantics(html: string): string {
        if (!html.includes(TABLE_BORDERLESS_CLASS)) return html;
        try {
            const doc = new DOMParser().parseFromString(
                `<div data-letter-inline-root>${html}</div>`,
                'text/html',
            );
            const root = doc.querySelector('[data-letter-inline-root]');
            if (!root) return html;

            root.querySelectorAll(`table.${TABLE_BORDERLESS_CLASS}`).forEach((node) => {
                const el = node as HTMLElement;
                el.style.setProperty('border', 'none');
                el.style.setProperty('width', '100%');
                el.style.setProperty('border-collapse', 'collapse');
            });

            root.querySelectorAll(`table.${TABLE_BORDERLESS_CLASS} td, table.${TABLE_BORDERLESS_CLASS} th`).forEach(
                (node) => {
                    const el = node as HTMLElement;
                    const cls = new Set(
                        el.className
                            .split(/\s+/)
                            .map((c) => c.trim())
                            .filter(Boolean),
                    );
                    if (cls.has(TABLE_BORDERLESS_BLOCK_CLASS)) {
                        el.style.setProperty('border', 'none');
                        el.style.setProperty('padding', '0');
                    }
                    if (cls.has(TABLE_TEXT_RIGHT_CLASS)) {
                        el.style.setProperty('text-align', 'right');
                    }
                    if (cls.has(TABLE_TEXT_CENTER_CLASS)) {
                        el.style.setProperty('text-align', 'center');
                    }
                },
            );

            return root.innerHTML;
        } catch {
            return html;
        }
    }

    /**
     * True only for letter `.table-borderless` tables ? not markdown / chat tables.
     * Those use borderlessCompactTable in pdfmake; other tables keep borders from HTML or
     * {@link applyInlineQuillTableBorders}.
     */
    private static isBorderlessZeroPaddingLayoutTable(table: HTMLTableElement): boolean {
        if (table.classList.contains(QUILL_HR_EXPORT_LINE_CLASS)) return false;
        return table.classList.contains(TABLE_BORDERLESS_CLASS);
    }

    private static applyBorderlessLetterTablePdfLayout(root: HTMLElement): void {
        root.querySelectorAll('table').forEach((node) => {
            const table = node as HTMLTableElement;
            if (!QuillHelper.isBorderlessZeroPaddingLayoutTable(table)) return;
            const spec = { layout: 'borderlessCompactTable' };
            const existing = table.getAttribute('data-pdfmake');
            if (existing) {
                try {
                    const parsed = JSON.parse(
                        existing.replace(/'/g, '"'),
                    ) as Record<string, unknown>;
                    if (parsed.layout == null) parsed.layout = 'borderlessCompactTable';
                    table.setAttribute('data-pdfmake', JSON.stringify(parsed));
                } catch {
                    table.setAttribute('data-pdfmake', JSON.stringify(spec));
                }
            } else {
                table.setAttribute('data-pdfmake', JSON.stringify(spec));
            }
        });
    }

    /**
     * Quill tables rely on .ql-editor CSS for borders; Word/PDF HTML has no stylesheet. Inline borders
     * so table grids render in Word and html-to-pdfmake sees cell boundaries.
     */
    private static applyInlineQuillTableBorders(root: HTMLElement): void {
        const tableOutline = '1pt solid #d1d5db';
        const cellBorder = '1pt solid #d1d5db';
        const cellPadding = '6pt';

        const hasBorderStyle = (el: HTMLElement): boolean => {
            const s = el.style;
            if (s.border && s.border !== 'none' && s.border !== 'hidden') return true;
            if (s.borderWidth && s.borderWidth !== '0px' && s.borderWidth !== '0') return true;
            for (const side of ['Top', 'Right', 'Bottom', 'Left'] as const) {
                const bx = (s as any)[`border${side}`];
                const bw = (s as any)[`border${side}Width`];
                if (bx && bx !== 'none') return true;
                if (bw && bw !== '0px' && bw !== '0') return true;
            }
            const attr = el.getAttribute('style') || '';
            if (/border\s*:\s*(?!none\b)(?!0\b)/i.test(attr)) return true;
            if (/border-(top|right|bottom|left)\s*:\s*(?!none\b)/i.test(attr)) return true;
            return el.hasAttribute('border') && el.getAttribute('border') !== '0';
        };

        root.querySelectorAll('table').forEach((node) => {
            const table = node as HTMLElement;
            if (table.classList.contains(QUILL_HR_EXPORT_LINE_CLASS)) return;
            if (QuillHelper.isBorderlessZeroPaddingLayoutTable(table as HTMLTableElement))
                return;

            if (!table.style.borderCollapse) {
                table.style.borderCollapse = 'collapse';
            }
            if (!hasBorderStyle(table)) {
                if (!table.style.border) table.style.border = tableOutline;
            }

            table.querySelectorAll('td, th').forEach((cellNode) => {
                const cell = cellNode as HTMLElement;
                if (!hasBorderStyle(cell)) {
                    cell.style.border = cellBorder;
                }
                if (!cell.style.padding) {
                    cell.style.padding = cellPadding;
                }
                if (cell.tagName === 'TH') {
                    if (!cell.style.backgroundColor) {
                        cell.style.backgroundColor = '#f3f4f6';
                    }
                    if (!cell.style.fontWeight) {
                        cell.style.fontWeight = '700';
                    }
                }
            });
        });
    }

    /**
     * Normalizes font sizes and element styling for the PDF body pipeline.
     *
     * &lt;hr&gt; tags are left in place so html-to-pdfmake emits a line canvas, which
     * {@link convertHrCanvasToTableLine} then turns into a visible table rule.
     */
    private static processHtmlContent(html: string): string {
        const tempDiv = document.createElement('div');
        tempDiv.innerHTML = html;
        const defaultFontSizeCss = `${FILE_EXPORT_PARAMS.DEFAULT_FONT_SIZE}px`;

        // Normalize heading font sizes and line heights
        Object.entries(EDITOR_HEADING_STYLES)
            .filter(([k]) => k !== 'false')
            .forEach(([level, entry]) => {
                tempDiv.querySelectorAll(`h${level}`).forEach((heading: Element) => {
                    const htmlHeading = heading as HTMLElement;
                    htmlHeading.style.fontSize = `${entry.fontSize}px`;
                    htmlHeading.style.fontWeight = String(entry.fontWeight);
                    if (!htmlHeading.style.lineHeight) {
                        htmlHeading.style.lineHeight = String(entry.lineHeight);
                    }
                });
            });
            
        // Process paragraphs and blocks (p, div, etc.) - preserve alignment and indentation.
        // Include `li`: Quill nested lists use ql_indent-* (legacy: ql-indent-*) on list items.
        const blockElements = tempDiv.querySelectorAll('p, div, h1, h2, h3, h4, h5, h6, li');
        blockElements.forEach((element: Element) => {
            const htmlElement = element as HTMLElement;
            
            // Set font size and reduce spacing for paragraphs
            if (element.tagName === 'P') {
                htmlElement.style.fontSize = defaultFontSizeCss;
                // Reduce spacing between paragraphs
                htmlElement.style.marginTop = '0';
                htmlElement.style.marginBottom = '0';
                htmlElement.style.paddingTop = '0';
                htmlElement.style.paddingBottom = '0';
            }
            
            // Preserve alignment from Quill classes or existing styles (handle both ql- and ql_ prefixes)
            const classList = element.classList;
            if (!htmlElement.style.textAlign) {
                if (classList.contains('ql_align-center') || classList.contains('ql-align-center')) {
                    htmlElement.style.textAlign = 'center';
                } else if (classList.contains('ql_align-right') || classList.contains('ql-align-right')) {
                    htmlElement.style.textAlign = 'right';
                } else if (classList.contains('ql_align-justify') || classList.contains('ql-align-justify')) {
                    htmlElement.style.textAlign = 'justify';
                } else if (classList.contains('ql_align-left') || classList.contains('ql-align-left')) {
                    htmlElement.style.textAlign = 'left';
                }
            }

            // Preserve indentation (handle both ql-indent-* and ql_indent-* prefixes)
            const indentMatch = Array.from(classList).find(
                cls => cls.startsWith('ql_indent-') || cls.startsWith('ql-indent-'),
            );
            if (indentMatch) {
                const indentLevel = parseInt(indentMatch.replace(/^(?:ql_|ql-)indent-/, '')) || 0;
                if (indentLevel > 0) {
                    htmlElement.style.paddingLeft = `${indentLevel * 3}em`;
                }
            }
        });

        // Expand ql_ / ql-* classes to inline styles for PDF export.
        // html-to-pdfmake reads inline styles only; it cannot use our CSS sheet.
        tempDiv.querySelectorAll('[class]').forEach((element: Element) => {
            const htmlElement = element as HTMLElement;
            htmlElement.classList.forEach(cls => {
                if (cls.startsWith('ql_size-') || cls.startsWith('ql-size-')) {
                    if (!htmlElement.style.fontSize) {
                        htmlElement.style.fontSize = cls.replace(/^(?:ql_|ql-)size-/, '');
                    }
                    return;
                }
                const styles = QuillHelper.ql_INLINE_STYLES[cls];
                if (!styles) return;
                Object.entries(styles).forEach(([prop, val]) => {
                    // <u> and <s>/<del> tags are handled natively by html-to-pdfmake (defaultStyles).
                    // Adding text-decoration as an inline style on top causes the library to push
                    // the decoration value a second time (no dedup on element styles, only on tag defaults),
                    // resulting in decoration:['underline','underline'] which pdfmake renders as an
                    // extra full-width blank underline/strikethrough line beyond the actual text.
                    if (prop === 'text-decoration') {
                        const tag = htmlElement.tagName;
                        if (val === 'underline' && tag === 'U') return;
                        if (val === 'line-through' && (tag === 'S' || tag === 'DEL')) return;
                    }
                    if (!htmlElement.style.getPropertyValue(prop)) {
                        htmlElement.style.setProperty(prop, val);
                    }
                });
            });
        });

        // Blockquotes: convert to a single-cell table so html-to-pdfmake renders the left border.
        // html-to-pdfmake only translates CSS borders on <td>/<th>, not on block elements like <blockquote>.
        const bqStyles = FILE_EXPORT_PARAMS.BLOCKQUOTE_STYLES;
        tempDiv.querySelectorAll('blockquote').forEach((blockquote: Element) => {
            const htmlBlockquote = blockquote as HTMLElement;
            const table = document.createElement('table');
            table.setAttribute('style', 'border-collapse:collapse;width:100%;margin:4px 0;');
            const tr = document.createElement('tr');
            const td = document.createElement('td');
            td.setAttribute('style',
                `border-left:${bqStyles.borderLeft};` +
                `border-top:none;border-right:none;border-bottom:none;` +
                `padding-left:${bqStyles.paddingLeft};padding-top:0;padding-right:0;padding-bottom:0;` +
                `color:${bqStyles.color};font-style:${bqStyles.fontStyle};` +
                `font-size:${defaultFontSizeCss};`
            );
            // Stamp inherited styles on children so html-to-pdfmake picks them up per-run
            htmlBlockquote.querySelectorAll('*').forEach((child) => {
                const el = child as HTMLElement;
                if (!el.style.fontSize) el.style.fontSize = defaultFontSizeCss;
                if (!el.style.color) el.style.color = bqStyles.color;
                if (!el.style.fontStyle) el.style.fontStyle = bqStyles.fontStyle;
            });
            while (htmlBlockquote.firstChild) td.appendChild(htmlBlockquote.firstChild);
            tr.appendChild(td);
            table.appendChild(tr);
            htmlBlockquote.parentNode?.replaceChild(table, htmlBlockquote);
        });

        // Ensure subscript and superscript tags are preserved
        // Quill uses <sub> and <sup> tags which should be handled by html-to-pdfmake
        // But we'll ensure they have proper styling
        // Word renders super/subscript at ~65% of the body font. Use the same ratio so PDF matches.
        const subSupSizePx = `${Math.round(FILE_EXPORT_PARAMS.DEFAULT_FONT_SIZE * 0.65)}px`;

        const subElements = tempDiv.querySelectorAll('sub');
        subElements.forEach((sub: Element) => {
            const htmlSub = sub as HTMLElement;
            htmlSub.style.fontSize = subSupSizePx;
            htmlSub.style.verticalAlign = 'sub';
            htmlSub.setAttribute('data-pdfmake-sub', 'true');
        });

        const supElements = tempDiv.querySelectorAll('sup');
        supElements.forEach((sup: Element) => {
            const htmlSup = sup as HTMLElement;
            htmlSup.style.fontSize = subSupSizePx;
            htmlSup.style.verticalAlign = 'super';
            htmlSup.setAttribute('data-pdfmake-sup', 'true');
        });

        // Quill 2 outputs both bullet and ordered lists as <ol>; list type is on <li data-list="bullet"> or data-list="ordered".
        // Convert <ol> to <ul> for bullet lists so html-to-pdfmake renders bullets instead of numbers.
        tempDiv.querySelectorAll('ol').forEach((ol) => {
            const firstLi = ol.querySelector('li');
            if (firstLi?.getAttribute('data-list') === 'bullet') {
                const ul = document.createElement('ul');
                while (ol.firstChild) ul.appendChild(ol.firstChild);
                ol.parentNode?.replaceChild(ul, ol);
            }
        });

        // Normalize list font size so ul/ol/li match body text (html-to-pdfmake otherwise can render lists larger)
        const listElements = tempDiv.querySelectorAll('ul, ol, li');
        listElements.forEach((el) => {
            const htmlEl = el as HTMLElement;
            htmlEl.style.fontSize = defaultFontSizeCss;
        });

        // Markdown tables often use utility classes (e.g. text-sm) without inline font-size; html-to-pdfmake can
        // then render cell text larger than normalized paragraphs. Force body font size on table structure.
        const tableElements = tempDiv.querySelectorAll(
            'table, thead, tbody, tfoot, tr, th, td, caption',
        );
        tableElements.forEach((el) => {
            const tableEl = el as HTMLElement;
            tableEl.style.fontSize = defaultFontSizeCss;
        });

        // html-to-pdfmake maps `background-color` on TH/TD ? table cell fillColor. The `background` shorthand becomes
        // pdfmake's text `background` (rectangle behind each text run) and often renders as an opaque black bar.
        tempDiv.querySelectorAll('table, thead, tbody, tr, th, td').forEach((el) => {
            const h = el as HTMLElement;
            const bgShorthand = h.style.background;
            if (!bgShorthand) return;
            const existing = h.style.backgroundColor;
            if (
                !existing ||
                existing === 'transparent' ||
                existing === 'rgba(0, 0, 0, 0)'
            ) {
                const hex = bgShorthand.match(/#([0-9a-f]{3}|[0-9a-f]{6})\b/i)?.[0];
                const rgb = bgShorthand.match(/rgba?\([^)]+\)/i)?.[0];
                if (hex) h.style.backgroundColor = hex;
                else if (rgb) h.style.backgroundColor = rgb;
            }
            h.style.background = '';
        });

        // Normalize image sizes
        tempDiv.querySelectorAll('img').forEach((img: Element) => {
            const htmlImg = img as HTMLImageElement;
            htmlImg.style.height = 'auto';
            htmlImg.style.width = FILE_EXPORT_PARAMS.IMAGE_WIDTH;
        });

        QuillHelper.applyInlineQuillTableBorders(tempDiv);
        QuillHelper.applyBorderlessLetterTablePdfLayout(tempDiv);

        // quill-table-better inserts <temporary class="ql-table-temporary"> (invalid table child). Word HTML
        // can reserve a huge header/body band for it, especially in Protected View ? remove for export.
        tempDiv.querySelectorAll('temporary').forEach((el) => el.remove());

        // Replace checkmark (U+2713) with a glyph that renders in pdfmake's Roboto subset (? often missing)
        let result = tempDiv.innerHTML;
        result = result.replace(/\u2713/g, "\u221A"); // ? ? ? (square root, check-like, in common PDF fonts)
        return result;
    }

        /**
     * Converts Quill code-block HTML (`<pre><div class="ql-code-block">…</div></pre>`) to plain
     * `<p>` elements before the content is pasted into the editor. Quill's code-block format
     * disables all rich-text formatting, so content that arrives pre-formatted as code blocks
     * (e.g. from AI-generated HTML or a previous save) becomes un-styleable. Stripping the
     * code-block wrapper on load restores normal formatting behaviour.
     * 
     * This case comes from update signature popup where signature input is a non styling quill editor
     */
    static stripCodeBlocksToPlainParagraphs(html: string): string {
        if (!html.includes('ql-code-block')) return html;
        try {
            const doc = new DOMParser().parseFromString(
                `<div data-strip-root>${html}</div>`,
                'text/html',
            );
            const root = doc.querySelector('[data-strip-root]');
            if (!root) return html;

            // Handle <pre> wrappers that contain one or more .ql-code-block children
            root.querySelectorAll('pre').forEach((pre) => {
                const blocks = pre.querySelectorAll('.ql-code-block');
                if (!blocks.length) return;
                const fragment = doc.createDocumentFragment();
                blocks.forEach((block) => {
                    const p = doc.createElement('p');
                    p.innerHTML = block.innerHTML;
                    fragment.appendChild(p);
                });
                pre.replaceWith(fragment);
            });

            // Handle any remaining bare .ql-code-block divs (not inside <pre>)
            root.querySelectorAll('.ql-code-block').forEach((block) => {
                const p = doc.createElement('p');
                p.innerHTML = block.innerHTML;
                block.replaceWith(p);
            });

            return root.innerHTML;
        } catch {
            return html;
        }
    }

    /**
     * Ensures text content in pdfmake format wraps properly
     */
    private static ensureTextWrapping(content: any): any {
        if (Array.isArray(content)) {
            return content.map(item => QuillHelper.ensureTextWrapping(item));
        }

        if (typeof content === 'object' && content !== null) {
            const processed: any = { ...content };

            // Ensure text wraps for string content
            if (typeof processed.text === 'string' && processed.text.length > 0) {
                processed.wordWrap = 'break-word';
                processed.noWrap = false;
            }

            // Recursively process nested content
            if (processed.stack) {
                processed.stack = QuillHelper.ensureTextWrapping(processed.stack);
            }
            if (Array.isArray(processed.text)) {
                processed.text = processed.text.map((item: any) => QuillHelper.ensureTextWrapping(item));
            }
            if (processed.ul) {
                processed.ul = QuillHelper.ensureTextWrapping(processed.ul);
            }
            if (processed.ol) {
                processed.ol = QuillHelper.ensureTextWrapping(processed.ol);
            }

            if (processed.table && typeof processed.table === 'object' && Array.isArray(processed.table.body)) {
                processed.table = {
                    ...processed.table,
                    body: processed.table.body.map((row: unknown) => {
                        if (!Array.isArray(row)) {
                            return QuillHelper.ensureTextWrappingInTableCell(row);
                        }
                        return row.map((cell: unknown) =>
                            QuillHelper.ensureTextWrappingInTableCell(cell),
                        );
                    }),
                };
            }

            if (Array.isArray(processed.columns)) {
                processed.columns = processed.columns.map((col: any) =>
                    QuillHelper.ensureTextWrapping(col),
                );
            }

            return processed;
        }

        return content;
    }

    /**
     * Table cells from html-to-pdfmake often keep {@code noWrap: true}, which draws all text on one line
     * and overlaps neighbouring columns. Coerce string cells and clear noWrap after normal recursion.
     */
    private static ensureTextWrappingInTableCell(cell: any): any {
        if (cell === null || cell === undefined) return cell;
        if (typeof cell === 'string') {
            return cell.length === 0 ? cell : { text: cell, noWrap: false };
        }
        if (Array.isArray(cell)) {
            return cell.map((c) => QuillHelper.ensureTextWrappingInTableCell(c));
        }
        if (typeof cell === 'object') {
            const out = QuillHelper.ensureTextWrapping(cell);
            if (out && typeof out === 'object' && (out as { noWrap?: boolean }).noWrap === true) {
                (out as { noWrap: boolean }).noWrap = false;
            }
            return out;
        }
        return cell;
    }

    /**
     * Detects pdfmake objects that are HR (canvas with a single line from html-to-pdfmake)
     * and replaces them with a table-based horizontal line so the line respects container width.
     * html-to-pdfmake converts <hr> to a canvas with fixed width (514pt), which doesn't display
     * correctly when the content is inside a narrower footer/header cell.
     */
    private static convertHrCanvasToTableLine(content: any): any {
        if (Array.isArray(content)) {
            return content.map((item: any) => QuillHelper.convertHrCanvasToTableLine(item));
        }

        if (typeof content === 'object' && content !== null) {
            const canvas = content.canvas;
            const isHrCanvas =
                Array.isArray(canvas) &&
                canvas.length === 1 &&
                canvas[0]?.type === 'line' &&
                content.text === undefined;

            if (isHrCanvas) {
                const line = canvas[0];
                const rawColor = line.lineColor as string | undefined;
                const lineColor =
                    !rawColor ||
                    String(rawColor).toLowerCase() === '#000000' ||
                    String(rawColor).toLowerCase() === 'black'
                        ? FILE_EXPORT_PARAMS.HR_LINE_COLOR
                        : rawColor;
                const lineWidth = line.lineWidth ?? 0.5;
                const margin = FILE_EXPORT_PARAMS.HR_LINE_MARGINS;
                // Use custom layout so the line is actually drawn (cell borders with noBorders layout are not drawn)
                return {
                    margin,
                    table: {
                        widths: ['*'],
                        body: [
                            [
                                {
                                    text: ' ',
                                    fontSize: 1,
                                    lineHeight: 1,
                                },
                            ],
                        ],
                    },
                    layout: 'hrLineLayout',
                    hrLineWidth: lineWidth,
                    hrLineColor: lineColor,
                };
            }

            const processed: any = { ...content };
            if (processed.stack) {
                processed.stack = QuillHelper.convertHrCanvasToTableLine(processed.stack);
            }
            if (Array.isArray(processed.text)) {
                processed.text = processed.text.map((item: any) =>
                    QuillHelper.convertHrCanvasToTableLine(item)
                );
            }
            if (processed.ul) {
                processed.ul = QuillHelper.convertHrCanvasToTableLine(processed.ul);
            }
            if (processed.ol) {
                processed.ol = QuillHelper.convertHrCanvasToTableLine(processed.ol);
            }
            if (processed.table?.body) {
                processed.table = {
                    ...processed.table,
                    body: QuillHelper.convertHrCanvasToTableLine(processed.table.body),
                };
            }
            return processed;
        }

        return content;
    }

    /**
     * Creates pdfmake heading styles configuration
     */
    private static createHeadingStyles(): Record<string, any> {
        return Object.fromEntries(
            Object.entries(EDITOR_HEADING_STYLES)
                .filter(([k]) => k !== 'false')
                .map(([k, v]) => [`html-h${k}`, { fontSize: v.fontSize, bold: v.fontWeight >= 600, lineHeight: v.lineHeight }])
        );
    }

    /**
     * Generates a filesystem-safe filename from current local date/time.
     */
    static generateFileName(): string {
        return dayjs().format("YYYY_MM_DD_HH_mm_ss");
    }

    /**
     * Maps emojis and special Unicode symbols to glyphs supported by pdfmake (Roboto VFS),
     * preventing unsupported characters from rendering as missing-glyph boxes.
     */
    private static emojiToPdfSafeChars(text: string): string {
        let s = text;
        try {
            s = s.normalize('NFC');
        } catch {
            /** */
        }
        return (
            s
                .replace(/\u2705(?:\uFE0F|\uFE0E)?/g, '\u221A') // ? ? ?
                .replace(/\u274C(?:\uFE0F|\uFE0E)?/g, 'x') // ? ? ASCII x
                .replace(/\u2714\uFE0F?/g, '\u221A')
                .replace(/\u2716\uFE0F?/g, 'x')
                .replace(/\u2713/g, '\u221A')
                .replace(/\u2717/g, 'x')
                .replace(/\u2611\uFE0F?/g, '\u221A')
                .replace(/\u2610\uFE0F?/g, '-')
                .replace(/\u2612\uFE0F?/g, 'x')
        );
    }

    /**
     * Recursively processes pdfmake content to apply indentation and fix subscript/superscript
     * This ensures that marginLeft from HTML is properly converted to pdfmake margin arrays
     * and that sub/sup tags are converted to pdfmake format
     */
    private static applyIndentationToPdfContent(content: any): any {
        if (Array.isArray(content)) {
            return content.map(item => QuillHelper.applyIndentationToPdfContent(item));
        }

        if (typeof content === 'object' && content !== null) {
            const processed: any = { ...content };

            // Paragraph detection: html-to-pdfmake sets nodeName to uppercase ('P'), and style to ['html-p', ...].
            // Do NOT treat arbitrary { text: string } fragments as paragraphs ? <br> becomes { text: '\n' }, and
            // applying paragraph margins there duplicates vertical spacing between lines.
            const nodeNameLc = String(processed.nodeName ?? '').toLowerCase();
            const styleList = Array.isArray(processed.style)
                ? processed.style
                : processed.style != null
                  ? [processed.style]
                  : [];
            const hasHtmlPStyle = styleList.some(
                (s: unknown) => String(s).toLowerCase() === 'html-p',
            );
            const isParagraph =
                processed.tag === 'p' ||
                nodeNameLc === 'p' ||
                processed.type === 'p' ||
                processed.nodeType === 'p' ||
                hasHtmlPStyle;

            // If marginLeft is set as a string (from HTML), convert it to pdfmake margin array
            if (processed.marginLeft && typeof processed.marginLeft === 'string') {
                const marginValue = processed.marginLeft;
                // Parse the margin value (e.g., "3em", "42px")
                const match = marginValue.match(/([\d.]+)(px|em|pt)/);
                if (match) {
                    const value = parseFloat(match[1]);
                    const unit = match[2];
                    
                    // Convert to points (pdfmake uses points)
                    let points = 0;
                    if (unit === 'em') {
                        points = value * FILE_EXPORT_PARAMS.DEFAULT_FONT_SIZE * 0.75; // em to points
                    } else if (unit === 'px') {
                        points = value * 0.75; // px to points
                    } else if (unit === 'pt') {
                        points = value;
                    }
                    
                    if (points > 0) {
                        // Set margin array [left, top, right, bottom]
                        // Preserve paragraph top/bottom margins if this is a paragraph
                        if (isParagraph) {
                            processed.margin = [
                                points, 
                                FILE_EXPORT_PARAMS.PARAGRAPH_MARGINS[1], 
                                FILE_EXPORT_PARAMS.PARAGRAPH_MARGINS[2], 
                                FILE_EXPORT_PARAMS.PARAGRAPH_MARGINS[3]
                            ]; // [left, top, right, bottom]
                        } else if (processed.margin && Array.isArray(processed.margin)) {
                            processed.margin[0] = (processed.margin[0] || 0) + points;
                        } else {
                            processed.margin = [points, 0, 0, 0];
                        }
                    }
                }
                delete processed.marginLeft;
            }

            // Reduce paragraph spacing - set minimal margins for paragraphs
            // Do this after marginLeft processing to ensure it's not overwritten
            if (isParagraph) {
                // Always ensure paragraph margins are set correctly
                if (!processed.margin || !Array.isArray(processed.margin)) {
                    processed.margin = FILE_EXPORT_PARAMS.PARAGRAPH_MARGINS; // [left, top, right, bottom]
                } else {
                    // Preserve left margin (for indentation) but always set top/bottom to 6
                    const leftMargin = processed.margin[0] || 0;
                    processed.margin = [
                        leftMargin, 
                        FILE_EXPORT_PARAMS.PARAGRAPH_MARGINS[1], 
                        FILE_EXPORT_PARAMS.PARAGRAPH_MARGINS[2], 
                        FILE_EXPORT_PARAMS.PARAGRAPH_MARGINS[3]
                    ]; // [left, top, right, bottom]
                }
            }

            // Inject lineHeight for heading nodes so pdfmake renders correct spacing.
            // html-to-pdfmake emits html-h1..html-h6 style names; named styles are set by
            // createHeadingStyles(), but the per-node lineHeight is also needed directly.
            const headingLevel = nodeNameLc.match(/^h([1-6])$/)?.[1];
            if (headingLevel) {
                const headingEntry = EDITOR_HEADING_STYLES[headingLevel];
                if (headingEntry && !processed.lineHeight) {
                    processed.lineHeight = headingEntry.lineHeight;
                }
            }

            // Handle subscript and superscript
            // Check for sup FIRST to avoid it being misidentified as sub
            // html-to-pdfmake might preserve tag names or use different properties
            const isSup = processed.tag === 'sup' ||
                         processed.nodeName === 'sup' || 
                         processed.type === 'sup' ||
                         processed.verticalAlign === 'super' ||
                         (processed.style && processed.style.verticalAlign === 'super') ||
                         processed['data-pdfmake-sup'] === 'true' ||
                         processed['data-pdfmake-sup'] === true;
            
            const isSub = processed.tag === 'sub' || 
                         processed.nodeName === 'sub' || 
                         processed.type === 'sub' ||
                         processed.verticalAlign === 'sub' ||
                         (processed.style && processed.style.verticalAlign === 'sub') ||
                         processed['data-pdfmake-sub'] === 'true' ||
                         processed['data-pdfmake-sub'] === true;

            // Match Word: super/subscript rendered at ~65% of body font size.
            const subSupFontSize = Math.round(FILE_EXPORT_PARAMS.DEFAULT_FONT_SIZE * 0.65);

            if (isSup) {
                processed.sup = true;
                delete processed.sub;
                processed.fontSize = subSupFontSize;
                delete processed.tag;
                delete processed.nodeName;
                delete processed.type;
                delete processed.verticalAlign;
                delete processed['data-pdfmake-sup'];
                if (processed.style) {
                    delete processed.style.verticalAlign;
                }
            } else if (isSub) {
                processed.sub = true;
                delete processed.sup;
                processed.fontSize = subSupFontSize;
                delete processed.tag;
                delete processed.nodeName;
                delete processed.type;
                delete processed.verticalAlign;
                delete processed['data-pdfmake-sub'];
                if (processed.style) {
                    delete processed.style.verticalAlign;
                }
            }

            // Recursively process nested content
            if (processed.stack) {
                processed.stack = QuillHelper.applyIndentationToPdfContent(processed.stack);
            }
            if (processed.ul) {
                processed.ul = QuillHelper.applyIndentationToPdfContent(processed.ul);
            }
            if (processed.ol) {
                processed.ol = QuillHelper.applyIndentationToPdfContent(processed.ol);
            }
            if (processed.table && processed.table.body) {
                processed.table.body = QuillHelper.applyIndentationToPdfContent(processed.table.body);
            }
            // Process text arrays that might contain sub/sup elements or paragraph content
            if (Array.isArray(processed.text)) {
                processed.text = processed.text.map((item: any) => {
                    if (typeof item === 'object' && item !== null) {
                        const processedItem = QuillHelper.applyIndentationToPdfContent(item);
                        // Double-check for superscript in nested items
                        if (processedItem.tag === 'sup' || processedItem.nodeName === 'sup' || processedItem.type === 'sup') {
                            processedItem.sup = true;
                            delete processedItem.sub;
                            delete processedItem.tag;
                            delete processedItem.nodeName;
                            delete processedItem.type;
                        }
                        return processedItem;
                    }
                    return item;
                });
            }
            
            // Also check if this is a paragraph-like structure (text with no other formatting)
            // and apply margins if it looks like a paragraph
            if (!isParagraph && typeof processed.text === 'string' && processed.text.trim() && 
                !processed.ul && !processed.ol && !processed.table && !processed.stack &&
                !processed.bold && !processed.italics && !processed.underline && 
                !processed.link && processed.text.length > 20) {
                // This might be a paragraph that html-to-pdfmake converted differently
                // Apply paragraph margins
                if (!processed.margin || !Array.isArray(processed.margin)) {
                    processed.margin = FILE_EXPORT_PARAMS.PARAGRAPH_MARGINS;
                } else {
                    const leftMargin = processed.margin[0] || 0;
                    processed.margin = [
                        leftMargin, 
                        FILE_EXPORT_PARAMS.PARAGRAPH_MARGINS[1], 
                        FILE_EXPORT_PARAMS.PARAGRAPH_MARGINS[2], 
                        FILE_EXPORT_PARAMS.PARAGRAPH_MARGINS[3]
                    ];
                }
            }

            return processed;
        }

        return content;
    }

    /**
     * Ensures all paragraph-like elements in content array have proper margins
     */
    private static ensureParagraphMargins(content: any[]): any[] {
        return content.map((item: any) => {
            if (typeof item === 'object' && item !== null) {
                const isParagraphTag =
                    item.tag === 'p' || item.nodeName === 'p' || item.type === 'p';
                // Do not treat empty / whitespace-only <p> as real paragraphs ? they still get huge margins.
                const looksLikeParagraph =
                    (isParagraphTag && QuillHelper.pdfTextContentIsMeaningful(item.text)) ||
                    (!isParagraphTag &&
                        typeof item.text === 'string' &&
                        item.text.trim() &&
                        !item.ul &&
                        !item.ol &&
                        !item.table &&
                        !item.stack &&
                        !item.bold &&
                        !item.italics &&
                        item.text.length > 10);
                
                if (looksLikeParagraph) {
                    const leftMargin = (item.margin && Array.isArray(item.margin)) ? (item.margin[0] || 0) : 0;
                    item.margin = [
                        leftMargin, 
                        FILE_EXPORT_PARAMS.PARAGRAPH_MARGINS[1], 
                        FILE_EXPORT_PARAMS.PARAGRAPH_MARGINS[2], 
                        FILE_EXPORT_PARAMS.PARAGRAPH_MARGINS[3]
                    ];
                }
                
                // Recursively process nested structures
                if (item.stack) {
                    item.stack = QuillHelper.ensureParagraphMargins(Array.isArray(item.stack) ? item.stack : [item.stack]);
                }
                if (Array.isArray(item.text)) {
                    item.text = item.text.map((textItem: any) => {
                        if (typeof textItem === 'object' && textItem !== null) {
                            return QuillHelper.ensureParagraphMargins([textItem])[0];
                        }
                        return textItem;
                    });
                }
            }
            return item;
        });
    }

    /** Shared html-to-pdfmake options for letter/chat body PDF (tighter &lt;p&gt; than library defaults). */
    private static getPdfBodyHtmlToPdfmakeOptions(): Record<string, unknown> {
        return {
            tableAutoSize: true,
            removeExtraBlanks: true,
            defaultStyles: {
                p: { margin: [...FILE_EXPORT_PARAMS.PDFMAKE_DEFAULT_P_MARGIN] },
            },
        };
    }

    /** True when &lt;p&gt; is empty, whitespace-only, or contains only &lt;br&gt; (Quill blank line). */
    private static isHtmlSpacerParagraph(p: HTMLParagraphElement): boolean {
        const isWhitespaceOnly = (s: string | null | undefined) =>
            !(s || '').replace(/[\s\u00a0\u200b]/g, '').length;

        for (const n of Array.from(p.childNodes)) {
            if (n.nodeType === Node.TEXT_NODE) {
                if (!isWhitespaceOnly(n.textContent)) return false;
            } else if (n.nodeType === Node.ELEMENT_NODE) {
                const el = n as HTMLElement;
                if (el.tagName === 'BR') continue;
                if (el.tagName === 'SPAN' || el.tagName === 'B' || el.tagName === 'STRONG') {
                    if (!isWhitespaceOnly(el.textContent)) return false;
                    continue;
                }
                return false;
            }
        }
        return true;
    }

    /**
     * Removes spacer &lt;p&gt; nodes (empty, whitespace-only, or only &lt;br&gt;) that Quill/markdown emit
     * between blocks. They collapse in the editor but become full paragraphs with margins in Word/PDF export.
     */
    private static stripSpacerParagraphsFromHtml(html: string): string {
        const container = document.createElement('div');
        container.innerHTML = html;

        container.querySelectorAll('p').forEach((p) => {
            if (QuillHelper.isHtmlSpacerParagraph(p as HTMLParagraphElement)) {
                p.remove();
            }
        });
        return container.innerHTML;
    }

    private static pdfTextContentIsMeaningful(text: unknown): boolean {
        if (text == null) return false;
        if (typeof text === 'string') {
            return !!text.replace(/[\s\u00a0\u200b\n\r]/g, '').length;
        }
        if (Array.isArray(text)) {
            return text.some((part) => {
                if (typeof part === 'string') {
                    return !!part.replace(/[\s\u00a0\u200b\n\r]/g, '').length;
                }
                if (part && typeof part === 'object' && 'text' in (part as object)) {
                    return QuillHelper.pdfTextContentIsMeaningful((part as { text: unknown }).text);
                }
                return true;
            });
        }
        return true;
    }

    private static isPdfWhitespaceOnlyParagraph(item: unknown): boolean {
        if (!item || typeof item !== 'object') return false;
        const o = item as Record<string, unknown>;
        if (o.table || o.canvas || o.image || o.ul || o.ol) return false;
        const isP =
            o.tag === 'p' || o.nodeName === 'p' || o.type === 'p';
        if (!isP) return false;
        return !QuillHelper.pdfTextContentIsMeaningful(o.text);
    }

    /** Drops spacer pdfmake paragraph objects left after conversion (e.g. lone \\n). */
    private static pruneWhitespaceOnlyParagraphsFromPdfContent(content: unknown): unknown {
        if (Array.isArray(content)) {
            return content
                .map((item) => QuillHelper.pruneWhitespaceOnlyParagraphsFromPdfContent(item))
                .filter((item) => item !== null && item !== undefined);
        }
        if (typeof content === 'object' && content !== null) {
            if (QuillHelper.isPdfWhitespaceOnlyParagraph(content)) {
                return null;
            }
            const o = content as Record<string, unknown>;
            const out: Record<string, unknown> = { ...o };
            if (Array.isArray(out.stack)) {
                out.stack = QuillHelper.pruneWhitespaceOnlyParagraphsFromPdfContent(out.stack);
            }
            if (Array.isArray(out.ul)) {
                out.ul = QuillHelper.pruneWhitespaceOnlyParagraphsFromPdfContent(out.ul);
            }
            if (Array.isArray(out.ol)) {
                out.ol = QuillHelper.pruneWhitespaceOnlyParagraphsFromPdfContent(out.ol);
            }
            if (o.table && typeof o.table === 'object' && Array.isArray((o.table as { body?: unknown }).body)) {
                const t = o.table as { body: unknown[]; [k: string]: unknown };
                out.table = {
                    ...t,
                    body: QuillHelper.pruneWhitespaceOnlyParagraphsFromPdfContent(t.body),
                };
            }
            if (Array.isArray(out.text)) {
                out.text = QuillHelper.pruneWhitespaceOnlyParagraphsFromPdfContent(out.text);
            }
            return out;
        }
        return content;
    }

    /**
     * Processes HTML to add marginLeft instead of paddingLeft for better pdfmake compatibility
     */
    private static convertPaddingToMargin(html: string): string {
        const tempDiv = document.createElement('div');
        tempDiv.innerHTML = html;

        const blockElements = tempDiv.querySelectorAll('p, div, h1, h2, h3, h4, h5, h6, li');
        blockElements.forEach((element: Element) => {
            const htmlElement = element as HTMLElement;
            const paddingLeft = htmlElement.style.paddingLeft;
            
            if (paddingLeft) {
                // Convert paddingLeft to marginLeft for pdfmake
                htmlElement.style.marginLeft = paddingLeft;
                htmlElement.style.paddingLeft = '';
            }
        });

        return tempDiv.innerHTML;
    }

    private static buildPreparedHtmlDocDefinition(html: string): any {
        let processedHtml = QuillHelper.processHtmlContent(html);
        processedHtml = applyChatPdfTableFitStylesToHtml(processedHtml);
        processedHtml = QuillHelper.convertPaddingToMargin(processedHtml);
        processedHtml = QuillHelper.stripSpacerParagraphsFromHtml(processedHtml);
        processedHtml = QuillHelper.emojiToPdfSafeChars(processedHtml);
        let pdfContent = htmlToPdfmake(
            processedHtml,
            QuillHelper.getPdfBodyHtmlToPdfmakeOptions() as any,
        );
        pdfContent = QuillHelper.convertHrCanvasToTableLine(pdfContent);
        pdfContent = QuillHelper.applyIndentationToPdfContent(pdfContent);
        pdfContent = QuillHelper.ensureTextWrapping(pdfContent);
        let contentArray: any[] = Array.isArray(pdfContent)
            ? pdfContent
            : [pdfContent];
        contentArray = QuillHelper.ensureParagraphMargins(contentArray);
        contentArray = QuillHelper.pruneWhitespaceOnlyParagraphsFromPdfContent(
            contentArray,
        ) as any[];

        const lineHeightValue = 1.2;
        const headingStyles = QuillHelper.createHeadingStyles();
        return {
            content: contentArray,
            defaultStyle: {
                fontSize: FILE_EXPORT_PARAMS.DEFAULT_FONT_SIZE,
                font: "Arial",
                lineHeight: lineHeightValue,
            },
            styles: {
                ...headingStyles,
                p: {
                    fontSize: FILE_EXPORT_PARAMS.DEFAULT_FONT_SIZE,
                    lineHeight: lineHeightValue,
                    margin: FILE_EXPORT_PARAMS.PARAGRAPH_MARGINS,
                },
                ul: {
                    fontSize: FILE_EXPORT_PARAMS.DEFAULT_FONT_SIZE,
                    lineHeight: lineHeightValue,
                },
                ol: {
                    fontSize: FILE_EXPORT_PARAMS.DEFAULT_FONT_SIZE,
                    lineHeight: lineHeightValue,
                },
                li: {
                    fontSize: FILE_EXPORT_PARAMS.DEFAULT_FONT_SIZE,
                    lineHeight: lineHeightValue,
                },
                blockquote: {
                    fontSize: FILE_EXPORT_PARAMS.DEFAULT_FONT_SIZE,
                    italics: true,
                    color: FILE_EXPORT_PARAMS.BLOCKQUOTE_STYLES.color,
                    margin: [16, 4, 0, 4],
                    border: [true, false, false, false],
                    borderColor: "#e5e7eb",
                    borderWidth: [4, 0, 0, 0],
                },
            },
            pageMargins: FILE_EXPORT_PARAMS.PAGE_MARGINS,
            removeTagClasses: false,
            window,
            footer: (currentPage: number, pageCount: number) => ({
                text: `Page ${currentPage} of ${pageCount}`,
                alignment: "right" as const,
                fontSize: 10,
                margin: FILE_EXPORT_PARAMS.FOOTER_MARGINS,
            }),
        };
    }

    /**
     * Builds and downloads a PDF from HTML already prepared for export (e.g. htmlForPdfFromRenderedHtml).
     * Body content only; footer shows page numbers.
     */
    static async downloadPdfFromPreparedHtml(
        html: string,
        fileNameBase?: string,
    ): Promise<void> {
        await QuillHelper.ensureArialFont();
        const base = (fileNameBase ?? QuillHelper.generateFileName()).replace(
            /\.pdf$/i,
            "",
        );
        const docDefinition = QuillHelper.buildPreparedHtmlDocDefinition(html);
        (pdfMake as any)
            .createPdf(docDefinition, pdfMakeApi.tableLayouts)
            .download(`${base}.pdf`);
    }

    // Inline-style equivalents of every ql_ class in LetterStylesheet.css.
    // Heading entries are derived from EDITOR_HEADING_STYLES (single source of truth).
    // CSS variables (--border, --primary, …) are resolved to static values usable in OOXML.
    private static readonly ql_INLINE_STYLES: Record<string, Record<string, string>> = {
        ...Object.fromEntries(
            Object.entries(EDITOR_HEADING_STYLES)
                .filter(([k]) => k !== 'false')
                .map(([k, v]) => [`ql_heading${k}`, {
                    'font-size': `${v.fontSize}px`,
                    'font-weight': `${v.fontWeight}`,
                    'line-height': `${v.lineHeight}`,
                }])
        ),
        'ql_normal':        { 'font-size': `${EDITOR_HEADING_STYLES['false'].fontSize}px`, 'font-weight': `${EDITOR_HEADING_STYLES['false'].fontWeight}` },
        'ql_bold':          { 'font-weight': '700' },
        'ql_italic':        { 'font-style': 'italic' },
        'ql_underline':     { 'text-decoration': 'underline' },
        'ql_strike':        { 'text-decoration': 'line-through' },
        'ql-align-left':      { 'text-align': 'left' },
        'ql-align-center':    { 'text-align': 'center' },
        'ql-align-right':     { 'text-align': 'right' },
        'ql-align-justify':   { 'text-align': 'justify' },
        'ql_align-left':    { 'text-align': 'left' },
        'ql_align-center':  { 'text-align': 'center' },
        'ql_align-right':   { 'text-align': 'right' },
        'ql_align-justify': { 'text-align': 'justify' },
        'ql_subscript':     { 'vertical-align': 'sub' },
        'ql_superscript':   { 'vertical-align': 'super' },
        'ql_table':         { 'border-collapse': 'collapse', 'border': '1pt solid #d1d5db', 'font-family': 'Arial, Helvetica, sans-serif', 'width': '100%' },
    };
}